import { App, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { Select } from '@style/global-style.app';

import SettingIcon from '@asset/settings.icon.svg?react';
import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { Input } from '@atom/form/input/input';
import { Text, Title } from '@atom/typography/typography';
import { newWebhookFormValidation } from '@component/forms/new-webhook/new-webhook.form-validation';
import { ConnectorServiceEnum } from '@enum/connector-service.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { Form } from '@form/common.style';
import { FormError } from '@form/form-error';
import { useNewWebhookForm } from '@form/new-webhook/new-webhook.form-hook';
import { useCurrentApplication } from '@hook/application/use-current-application.hook';
import { useStoreSelector } from '@hook/store/store.hooks';
import { useTriggerEventMutation } from '@hook/webhook/webhook.hooks';

import { FooterWrapper, NewWebhookButton, Wrapper } from './new-webhook/new-webhook.style';

export const TriggerEvent = () => {
  const { current } = useCurrentApplication();
  const error = useStoreSelector((state) => state.webhooks.error);
  const { data } = useStoreSelector((state) => state.consumers);
  const [isModalOpen, setModalOpen] = useState(false);
  const [eventId, setEventId] = useState(uuid());
  const [entityId, setEntityId] = useState(uuid());
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { enums, events } = useNewWebhookForm({ form });
  const [triggerWebhook] = useTriggerEventMutation();

  const serviceIds = Object.entries(ConnectorServiceEnum).map(([key, value]) => ({ label: key, value }));
  const consumers = Object.entries(data).map(([value, label]) => ({ label: label.name, value }));

  const onOpenModal = () => setModalOpen(true);
  const onCloseModal = () => setModalOpen(false);

  const triggerEvent = async () => {
    try {
      const event = await form.validateFields();
      const eventToSend = {
        eventType: event.eventType,
        unifiedApi: event.unifiedApi,
        serviceId: event.serviceId,
        consumerId: event.consumerId,
        eventId,
        entityId,
        occurredAt: new Date(),
        applicationId: current.id,
        deliveryUrl: event.deliveryUrl,
      };

      await triggerWebhook(eventToSend);

      form.resetFields();
      onCloseModal();
      message.success('Event sent');
    } catch {
      message.error('Event sending failed');
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      setEventId(uuid());
      setEntityId(uuid());
    }
  }, [isModalOpen]);

  return (
    <>
      <NewWebhookButton
        icon={<SettingIcon fill={ColorEnum.Black100} width={24} />}
        style={{ marginRight: '20px' }}
        type={ButtonTypeEnum.Primary}
        block
        ghost
        onClick={onOpenModal}
      >
        <Text color={ColorEnum.Black100} level='m' weight={400}>
          Trigger Test Event
        </Text>
      </NewWebhookButton>
      <Modal
        open={isModalOpen}
        width={640}
        footer={
          <FooterWrapper>
            <Button key='back' type={ButtonTypeEnum.Primary} ghost onClick={onCloseModal}>
              Cancel
            </Button>
            <Button key='submit' type={ButtonTypeEnum.Primary} onClick={() => form.validateFields().then(triggerEvent)}>
              Trigger Test Event
            </Button>
          </FooterWrapper>
        }
        title={
          <Title level={4} weight={400}>
            Trigger Test Event
          </Title>
        }
        onCancel={onCloseModal}
      >
        <Wrapper>
          <Form autoComplete='off' form={form} layout='vertical' name='webhook'>
            <Form.Item label='Delivery url' name='deliveryUrl' rules={[{ required: true }, newWebhookFormValidation]}>
              <Input placeholder='Please input delivery url' />
            </Form.Item>
            <Form.Item label='Event id'>
              <Input
                placeholder='Please input event id'
                value={eventId}
                onChange={({ target }) => setEventId(target.value)}
              />
            </Form.Item>
            <Form.Item label='Entity id'>
              <Input
                placeholder='Please input entity id'
                value={entityId}
                onChange={({ target }) => setEntityId(target.value)}
              />
            </Form.Item>
            <Form.Item label='Consumer' name='consumerId'>
              <Select options={consumers} />
            </Form.Item>
            <Form.Item label='Service id' name='serviceId' rules={[{ required: true }]}>
              <Select options={serviceIds} />
            </Form.Item>
            <Form.Item label='Unified API' name='unifiedApi' rules={[{ required: true }]}>
              <Select options={enums} />
            </Form.Item>
            <Form.Item label='Events' name='eventType' rules={[{ required: true }]}>
              <Select options={events} />
            </Form.Item>
            <FormError error={error} errorField='deliveryUrl' />
          </Form>
        </Wrapper>
      </Modal>
    </>
  );
};
